import { createContext, useContext } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type {
  ApproveMigrator,
  GetEip7702Delegation,
  GetEscapeHatchSignatures,
  Password,
  PrivateKeyRecoveryPayload,
  Prompt,
  RequestOAuth2,
  SignEip7702Revocation,
  SignTransfer,
  Sport as WalletSport,
} from '@sorare/wallet-shared';
import type {
  AuthorizationApproval,
  AuthorizationRequest,
} from '@sorare/wallet-shared/src/contexts/messaging/authorizations';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import type {
  UpdateUserAttributes,
  UpdateUserEmailAttributes,
} from '#src/contexts/auth/index.ts';
import type { RecoveryOption } from '#src/hooks/recovery/useRecoveryOptions.ts';
import type { GraphQLError } from '#src/lib/gql/index.ts';

export type Transfer = SignTransfer['request']['args']['transfer'];

type OAuth2Platform = RequestOAuth2['request']['args']['platform'];

export type WalletPlaceHolderRequestedSize = {
  height: number;
  width: number;
};

export type WalletPlaceHolderResizeHandler = (
  dimensions: WalletPlaceHolderRequestedSize
) => void;

export type OAuth2RequestHandler = (
  platform: OAuth2Platform,
  {
    nickname,
    sport,
    acceptTerms,
    acceptAgeLimit,
    acceptToShareAll,
    acceptToShareSpecific,
  }: {
    nickname?: string;
    sport?: Sport;
    acceptTerms?: boolean;
    acceptAgeLimit?: boolean;
    acceptToShareAll?: boolean;
    acceptToShareSpecific?: string[];
  }
) => Promise<void>;

type Eip7702RevocationAuthorization = NonNullable<
  SignEip7702Revocation['response']['result']
>['authorization'];

export type SignEip7702RevocationResult =
  | { authorization: Eip7702RevocationAuthorization; error?: never }
  | { error: string; authorization?: never };

interface WalletContext {
  setWindow: (window: Window | undefined) => void;
  signIn: () => Promise<void>;
  signUp: (username: string, sport: WalletSport) => Promise<void>;
  signUpMobileView: () => Promise<void>;
  passwordForgotten: () => Promise<void>;
  promptResetPassword: () => Promise<void>;
  logOut: () => Promise<void>;
  prompt: (type: Prompt['request']['args']['type']) => Promise<void>;
  promptDeposit: (starkRegistrationSignature?: string) => Promise<void>;
  promptRestoreWallet: (
    privateKeyRecoveryPayloads: PrivateKeyRecoveryPayload[],
    recoveryOption?: RecoveryOption
  ) => Promise<void>;
  selectedRecoveryOption: RecoveryOption | null;
  handleWalletSuccessullyRecovered: () => Promise<void>;
  getPassword: (
    args?: Password['request']['args']
  ) => Promise<string | undefined>;
  checkUserPhoneNumberVerificationCodeWithRecovery: (
    code: string
  ) => Promise<{ message: string }[] | null>;
  loadWallet: () => Promise<{ errors?: { message: string }[] }>;
  updateUserEmailWithPassword: (
    attributes: UpdateUserEmailAttributes
  ) => Promise<{
    errors: GraphQLError[];
  } | null>;
  updateUserWithPassword: (attributes: UpdateUserAttributes) => Promise<any>;
  approveMigrator: (
    nonce: number
  ) => Promise<ApproveMigrator['response']['result'] | undefined>;
  signMigration: (
    cardsIds: string[],
    expirationBlock: number | string
  ) => Promise<string | undefined>;
  signTransfer: (transfer: Transfer) => Promise<string | null>;
  getEscapeHatchSignatures: (args: {
    fee: bigint;
    feeRecipient: string;
  }) => Promise<GetEscapeHatchSignatures['response']['result'] | undefined>;
  getEip7702Delegation: () => Promise<
    GetEip7702Delegation['response']['result'] | undefined
  >;
  signEip7702Revocation: () => Promise<SignEip7702RevocationResult>;
  approveAuthorizationRequests: (
    authorizationRequests: AuthorizationRequest[]
  ) => Promise<{
    authorizationApprovals?: AuthorizationApproval[];
    starkKey?: string;
  }>;
  depositId?: string;
  signWalletChallenge: (
    challenge: string
  ) => Promise<{ r: string; s: string } | null>;
  walletNode: Element | null;
  setWalletNode: (elt: Element | null) => void;
  setOnWalletResizeRequest: (
    callback: WalletPlaceHolderResizeHandler
  ) => () => void;
  registerOAuthHandler: (callback: OAuth2RequestHandler) => () => void;
}

export const WalletContext = createContext<WalletContext | null>(null);

export const useWalletContext = () => useContext(WalletContext)!;
export const useMessagingContext = () => useContext(MessagingContext)!;
